/**
 * PublicBackorderQuotePage
 * ------------------------
 * Public, passwordless quote form keyed to a specific backorder ID.
 *
 * Route: /quote/backorder/:backorderId
 *
 * Suppliers land here after clicking the CTA in the RFQ email Sterile Mate sent
 * them. They enter their company info + per-item availability / price / lead
 * time and submit — backend auto-matches them against db.suppliers by email if
 * possible, otherwise stores the quote anonymously.
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, Send, ShieldCheck, Mail, Phone, Building2, MapPin, Package, Truck, CheckCircle2, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicBackorderQuotePage() {
  const { backorderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bo, setBo] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    supplier_name: "",
    supplier_email: "",
    supplier_contact: "",
    supplier_phone: "",
    total_estimate: "",
    lead_time: "",
    freight_estimate: "",
    notes: "",
    item_quotes: [],
  });

  // Load backorder summary
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/api/backorder-sourcing/${backorderId}/public`)
      .then((r) => {
        if (!alive) return;
        setBo(r.data);
        // Pre-populate per-item rows
        const items = r.data.items && r.data.items.length > 0
          ? r.data.items
          : [{ name: r.data.item_name || "Item", quantity: r.data.quantity || 1 }];
        setForm((f) => ({
          ...f,
          item_quotes: items.map((it) => ({
            name: it.name || it.product_name || "",
            quantity_requested: it.quantity || 1,
            qty_available: "",
            unit_price: "",
            lead_time: "",
            notes: "",
          })),
        }));
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "Backorder not found or has expired.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [backorderId]);

  const setItem = (idx, field, value) => {
    setForm((f) => {
      const next = [...f.item_quotes];
      next[idx] = { ...next[idx], [field]: value };
      return { ...f, item_quotes: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier_name.trim() || !form.supplier_email.trim()) {
      toast.error("Please enter your company name and email");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        supplier_name: form.supplier_name.trim(),
        supplier_email: form.supplier_email.trim(),
        supplier_contact: form.supplier_contact.trim() || null,
        supplier_phone: form.supplier_phone.trim() || null,
        item_quotes: form.item_quotes.map((it) => ({
          name: it.name,
          quantity_requested: Number(it.quantity_requested) || 0,
          qty_available: it.qty_available === "" ? null : Number(it.qty_available),
          unit_price: it.unit_price === "" ? null : Number(it.unit_price),
          lead_time: it.lead_time || null,
          notes: it.notes || null,
        })),
        total_estimate: form.total_estimate === "" ? null : Number(form.total_estimate),
        lead_time: form.lead_time || null,
        freight_estimate: form.freight_estimate === "" ? null : Number(form.freight_estimate),
        notes: form.notes || null,
      };
      await axios.post(`${API}/api/backorder-sourcing/${backorderId}/public-quote`, payload);
      setSubmitted(true);
      toast.success("Quote received — thank you!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit quote");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-slate-900 mb-2">Quote link not valid</h1>
          <p className="text-sm text-slate-600 mb-4">{error}</p>
          <Link to="/" className="text-amber-700 hover:text-amber-800 text-sm font-semibold inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Go to AskIsaac home
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl border border-emerald-200 p-10 max-w-lg text-center" data-testid="quote-submitted-card">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Quote received — thank you!</h1>
          <p className="text-sm text-slate-600 mb-6">
            Sterile Mate's sourcing desk has logged your response against backorder <span className="font-mono">#{backorderId}</span>.
            We&apos;ll follow up directly if we move forward with your offer.
          </p>
          <p className="text-xs text-slate-500">
            Questions? Reply to the email we sent you, or call <a className="text-amber-700 font-semibold" href="tel:2814563458">(281) 456-3458</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-amber-50/40">
      <header className="bg-[#0b1d3a] text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-amber-500 text-[#0b1d3a] flex items-center justify-center font-black">AI</div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-amber-300 font-bold">AskIsaac · Supplier Quote</p>
              <h1 className="text-lg font-bold">Sterile Mate Sourcing Desk</h1>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-amber-200">
            <ShieldCheck className="w-4 h-4" /> Passwordless · secure
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8" data-testid="public-quote-page">
        <div className="mb-6 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <Package className="w-5 h-5 text-amber-600 mt-1" />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">RFQ summary</p>
              <h2 className="text-lg font-bold text-slate-900 mb-2">
                Backorder <span className="font-mono text-amber-700">#{backorderId}</span>
              </h2>
              <div className="grid sm:grid-cols-2 gap-2 text-sm">
                <div className="flex items-start gap-1.5 text-slate-700">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span><strong>Ship-to:</strong> {bo?.ship_to}</span>
                </div>
                <div className="flex items-start gap-1.5 text-slate-700">
                  <Truck className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Freight terms:</strong>{" "}
                    {bo?.freight_terms
                      || (bo?.use_hospital_courier
                        ? "Third-party prepaid freight — do not quote freight"
                        : "FOB Destination, freight prepaid and add")}
                  </span>
                </div>
              </div>
              {bo?.notes && (
                <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                  <strong>Requestor notes:</strong> {bo.notes}
                </div>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {/* Supplier identity */}
          <section className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-amber-600" /> Your company
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Company name *</label>
                <input
                  required
                  value={form.supplier_name}
                  onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="pq-supplier-name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Reply-to email *</label>
                <input
                  required
                  type="email"
                  value={form.supplier_email}
                  onChange={(e) => setForm({ ...form, supplier_email: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="pq-supplier-email"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Contact name</label>
                <input
                  value={form.supplier_contact}
                  onChange={(e) => setForm({ ...form, supplier_contact: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Phone</label>
                <input
                  value={form.supplier_phone}
                  onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
          </section>

          {/* Per-item quote rows */}
          <section className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4 text-amber-600" /> Items requested
            </h3>
            <div className="space-y-3">
              {form.item_quotes.map((it, idx) => (
                <div key={idx} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50" data-testid={`pq-item-${idx}`}>
                  <div className="text-sm font-semibold text-slate-900 mb-2">
                    {idx + 1}. {it.name} <span className="text-xs text-slate-500 font-normal">· Qty requested: {it.quantity_requested}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold uppercase text-slate-500">Qty available</label>
                      <input
                        type="number"
                        min="0"
                        value={it.qty_available}
                        onChange={(e) => setItem(idx, "qty_available", e.target.value)}
                        className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold uppercase text-slate-500">Unit price (USD)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.unit_price}
                        onChange={(e) => setItem(idx, "unit_price", e.target.value)}
                        className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold uppercase text-slate-500">Lead time</label>
                      <input
                        placeholder="e.g. 3-5 days"
                        value={it.lead_time}
                        onChange={(e) => setItem(idx, "lead_time", e.target.value)}
                        className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold uppercase text-slate-500">Notes / substitution</label>
                      <input
                        placeholder="optional"
                        value={it.notes}
                        onChange={(e) => setItem(idx, "notes", e.target.value)}
                        className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Summary row */}
          <section className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">
              Summary & delivery
            </h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Total estimate (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.total_estimate}
                  onChange={(e) => setForm({ ...form, total_estimate: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Overall lead time</label>
                <input
                  placeholder="e.g. ships within 5 business days"
                  value={form.lead_time}
                  onChange={(e) => setForm({ ...form, lead_time: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">
                  {bo?.use_hospital_courier ? "Freight (not required)" : "Estimated freight (USD)"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  disabled={!!bo?.use_hospital_courier}
                  value={form.freight_estimate}
                  onChange={(e) => setForm({ ...form, freight_estimate: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1 disabled:bg-slate-100 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="sm:col-span-3">
                <label className="text-xs font-semibold text-slate-600">Additional notes</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything Sterile Mate's sourcing desk should know — substitutions, allotment caps, payment terms, etc."
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <p className="text-xs text-slate-500">
              Prefer to reply by email? Just hit reply on the message we sent you — both routes log to the same RFQ.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold shadow-sm disabled:opacity-60"
              data-testid="pq-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? "Submitting…" : "Submit quote"}
            </button>
          </div>
        </form>

        <div className="mt-8 text-center text-xs text-slate-500 space-x-3">
          <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> orders@sterilemate.com</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> (281) 456-3458</span>
        </div>
      </main>
    </div>
  );
}
